import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import {
  computeOrderCustomFees,
  Decimal,
  feeCalcOf,
  makePosCustomFeeType,
  type PosCustomFeeCalc,
  type PosCustomFeeType,
} from '@mlm/shared';
import { Button, Checkbox, Input } from '@mlm/ui';

/**
 * The custom fees ONE POS payment method carries: the charges a cashier can add
 * on top of a sale paid with that method (delivery, handling, tax, a card
 * surcharge). Rendered inside each method card on the POS payment methods page;
 * this used to be a standalone registry page shared by every tender.
 *
 * A fee is either typed by the cashier per sale (`manual`, a delivery fee varies
 * by distance) or pinned by the admin (`percent` / `flat`). Fees apply as an
 * ADDITIVE CASCADE in this list's order: each is computed on the running total
 * left by the ones above it, so a percentage stacks on top of an earlier fee.
 * That is why rows reorder and why the worked example below is worth reading.
 * Same shape as the cash-out deduction cascade, with the sign flipped.
 */
export function MethodFeesEditor({
  fees,
  onChange,
  canWrite,
}: {
  fees: PosCustomFeeType[];
  onChange: (next: PosCustomFeeType[]) => void;
  canWrite: boolean;
}) {
  function updateFee(i: number, patch: Partial<PosCustomFeeType>) {
    onChange(fees.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  }
  function addFee() {
    onChange([...fees, makePosCustomFeeType()]);
  }
  function removeFee(i: number) {
    onChange(fees.filter((_, idx) => idx !== i));
  }
  /** Reorder = re-sequence the cascade. No DnD library is used anywhere here. */
  function moveFee(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= fees.length) return;
    const next = [...fees];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  }

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground">
        A <span className="font-medium">typed</span> fee is keyed in per sale. A{' '}
        <span className="font-medium">percentage</span> or{' '}
        <span className="font-medium">fixed</span> fee is charged from the rate set here and the
        cashier can&apos;t change it, though they still choose which fees apply. Fees apply top to
        bottom, each on the running total. Removing or disabling one stops it being charged from now
        on; printed receipts keep their wording.
      </p>

      {fees.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No fees on this method. The cashier sees no custom-fee box when paying with it.
        </p>
      )}

      {fees.map((t, i) => (
        <FeeTypeCard
          key={i}
          index={i}
          total={fees.length}
          type={t}
          canWrite={canWrite}
          onChange={(patch) => updateFee(i, patch)}
          onRemove={() => removeFee(i)}
          onMove={(dir) => moveFee(i, dir)}
        />
      ))}

      {fees.length > 0 && <CascadePreview types={fees} />}

      {canWrite && (
        <Button variant="outline" size="sm" onClick={addFee}>
          <Plus className="h-4 w-4" /> Add fee
        </Button>
      )}
    </div>
  );
}

function FeeTypeCard({
  index,
  total,
  type: t,
  canWrite,
  onChange,
  onRemove,
  onMove,
}: {
  index: number;
  total: number;
  type: PosCustomFeeType;
  canWrite: boolean;
  onChange: (patch: Partial<PosCustomFeeType>) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const calc = feeCalcOf(t);
  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Code</label>
          <Input
            value={t.code}
            disabled={!canWrite}
            placeholder="delivery"
            onChange={(e) => onChange({ code: e.target.value.trim() })}
          />
          <p className="text-[11px] text-muted-foreground">
            Stored on the sale. Changing it later leaves past sales pointing at the old one.
          </p>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Label</label>
          <Input
            value={t.label}
            disabled={!canWrite}
            placeholder="Delivery"
            onChange={(e) => onChange({ label: e.target.value })}
          />
          <p className="text-[11px] text-muted-foreground">What prints on the receipt.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">How the amount is set</label>
          <select
            className="h-10 w-full rounded-md border bg-background px-2 text-sm"
            value={calc.kind}
            disabled={!canWrite}
            onChange={(e) => {
              const kind = e.target.value as PosCustomFeeCalc['kind'];
              onChange({
                calc:
                  kind === 'manual'
                    ? { kind: 'manual' }
                    : // Carry the number across so switching 10% -> flat keeps
                      // the figure the admin already typed.
                      { kind, value: calc.kind === 'manual' ? 0 : calc.value },
              });
            }}
          >
            <option value="manual">Cashier types it</option>
            <option value="percent">Percentage of the running total</option>
            <option value="flat">Fixed amount</option>
          </select>
          <p className="text-[11px] text-muted-foreground">
            {calc.kind === 'manual'
              ? 'The cashier keys in the amount on each sale.'
              : 'Charged automatically. The cashier still chooses whether it applies.'}
          </p>
        </div>
        {calc.kind !== 'manual' && (
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              {calc.kind === 'percent' ? 'Rate (%)' : 'Amount (₱)'}
            </label>
            <Input
              type="number"
              min="0"
              max={calc.kind === 'percent' ? '100' : undefined}
              step="0.01"
              value={String(calc.value)}
              disabled={!canWrite}
              placeholder={calc.kind === 'percent' ? '10' : '50.00'}
              onChange={(e) =>
                onChange({ calc: { kind: calc.kind, value: Number(e.target.value) || 0 } })
              }
            />
            <p className="text-[11px] text-muted-foreground">
              {calc.kind === 'percent'
                ? 'Charged on the sale total after discount, plus any fee above this one.'
                : 'The same amount on every sale it is added to.'}
            </p>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={t.enabled}
            disabled={!canWrite}
            onCheckedChange={(v) => onChange({ enabled: v === true })}
          />
          Enabled
        </label>
        {canWrite && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              disabled={index === 0}
              onClick={() => onMove(-1)}
              aria-label={`Move ${t.label || 'fee'} up`}
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={index === total - 1}
              onClick={() => onMove(1)}
              aria-label={`Move ${t.label || 'fee'} down`}
            >
              <ArrowDown className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onRemove}>
              <Trash2 className="h-4 w-4" /> Remove
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A worked example on a ₱500 sale, computed with the SAME function the till and
 * the server fold with — never a re-implementation, which is how a preview ends
 * up quoting a number the sale won't honour.
 *
 * Every enabled fee is shown applied, because the preview's job is to make the
 * cascade legible: each row prints what it was charged ON, so "10% of ₱550" is
 * visible rather than inferred. At the till the cashier still picks which fees
 * a given sale carries.
 *
 * A list of only typed fees has nothing to demonstrate (the amounts are
 * whatever the cashier keys in), so the example is limited to a note.
 */
const SAMPLE_GOODS = 500;

function CascadePreview({ types }: { types: PosCustomFeeType[] }) {
  const enabled = types.filter((t) => t.enabled && t.code.trim() !== '');
  const computed = enabled.filter((t) => feeCalcOf(t).kind !== 'manual');

  const priced = computeOrderCustomFees(
    new Decimal(SAMPLE_GOODS),
    computed.map((t) => ({ code: t.code, label: t.label || t.code, calc: feeCalcOf(t) })),
  );

  const peso = (v: Decimal | number | string) => `₱${new Decimal(v).toFixed(2)}`;

  // Re-walk the running total so each row can show its own base. `lines` is in
  // applied order and carries only positive fees, matching the fold exactly.
  let running = new Decimal(SAMPLE_GOODS);
  const rows = priced.lines.map((l) => {
    const base = running;
    running = running.plus(new Decimal(l.amount));
    return { code: l.code, label: l.label, amount: l.amount, base };
  });

  return (
    <div className="space-y-2 rounded-md border p-3">
      <p className="text-sm font-medium">Example: a {peso(SAMPLE_GOODS)} sale</p>
      {computed.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Every fee here is typed by the cashier, so there is nothing to work out in advance. Set
          one to a percentage or a fixed amount to see the running total.
        </p>
      ) : (
        <>
          <div className="space-y-1 font-mono text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Goods after discount</span>
              <span className="tabular-nums">{peso(SAMPLE_GOODS)}</span>
            </div>
            {rows.map((r, i) => {
              // Match on CODE (unique within the list), not the label, which an
              // admin may leave blank or repeat.
              const c = feeCalcOf(computed.find((t) => t.code === r.code) ?? {});
              return (
                <div key={i} className="flex justify-between">
                  <span className="text-muted-foreground">
                    {r.label}
                    {c.kind === 'percent' ? ` — ${c.value}% of ${peso(r.base)}` : ''}
                  </span>
                  <span className="tabular-nums">+{peso(r.amount)}</span>
                </div>
              );
            })}
            <div className="flex justify-between border-t pt-1 font-semibold">
              <span>Total</span>
              <span className="tabular-nums">
                {peso(new Decimal(SAMPLE_GOODS).plus(priced.total))}
              </span>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Typed fees are left out of this example — their amount is only known at the till, but
            they do count toward the running total a percentage below them is charged on.
          </p>
        </>
      )}
    </div>
  );
}
